import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    amount: { value: 0.18 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      float dist = distance(vUv, vec2(0.5));
      float vignette = smoothstep(0.95, 0.25, dist);
      color.rgb = mix(color.rgb, color.rgb * vignette, amount);
      gl_FragColor = color;
    }
  `
};

const ChromaticShader = {
  uniforms: {
    tDiffuse: { value: null },
    amount: { value: 0.00085 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;

    void main() {
      vec2 offset = vec2(amount, 0.0);
      float r = texture2D(tDiffuse, vUv + offset).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - offset).b;
      gl_FragColor = vec4(r, g, b, 1.0);
    }
  `
};

export class PostProcess {
  constructor({ renderer, scene, camera, enabled }) {
    this.enabled = enabled;
    this.renderer = renderer;

    if (!enabled) {
      this.composer = null;
      return;
    }

    this.composer = new EffectComposer(renderer.instance);
    this.renderPass = new RenderPass(scene, camera.instance);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.9,
      0.45,
      0.2
    );
    this.vignettePass = new ShaderPass(VignetteShader);
    this.chromaticPass = new ShaderPass(ChromaticShader);

    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.vignettePass);
    this.composer.addPass(this.chromaticPass);
  }

  setSize(width, height) {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
  }

  renderFrame(scene, camera) {
    if (this.composer) {
      this.composer.render();
      return;
    }

    this.renderer.render(scene, camera);
  }

  dispose() {
    if (!this.composer) {
      return;
    }

    this.composer.dispose();
    this.bloomPass.dispose();
  }
}
